import { isBrowserRenderableImage } from '../utils/browserImage';

const ALLOWED_UPLOAD_EXTENSIONS = new Set([
  'pdf',
  'png',
  'jpg',
  'jpeg',
  'doc',
  'docx',
]);

/** Same as TaskDocumentsPopover — PDF, images, Word */
export const UPLOAD_ACCEPT_ATTR = '.pdf,.png,.jpg,.jpeg,.doc,.docx';

function fileExtensionLower(filename: string): string {
  const lastDot = filename.lastIndexOf('.');
  if (lastDot <= 0 || lastDot === filename.length - 1) return '';
  return filename.slice(lastDot + 1).toLowerCase();
}

function mimeFromExtension(ext: string): string | undefined {
  switch (ext.toLowerCase()) {
    case 'pdf':
      return 'application/pdf';
    case 'png':
      return 'image/png';
    case 'jpg':
    case 'jpeg':
      return 'image/jpeg';
    case 'doc':
      return 'application/msword';
    case 'docx':
      return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    default:
      return undefined;
  }
}

export function inferMimeType(
  fileName: string | undefined,
  mime?: string | null
): string | undefined {
  if (mime && mime.trim() !== '') return mime;
  if (!fileName) return undefined;
  const ext = fileExtensionLower(fileName);
  return ext ? mimeFromExtension(ext) : undefined;
}

export type ViewerMode = 'pdf' | 'image' | 'office' | 'generic';

export interface PdfViewerDocShape {
  fileName?: string;
  customName?: string;
  mimeType?: string | null;
}

export function viewerModeForDocument(
  doc: PdfViewerDocShape | undefined
): ViewerMode {
  const label = doc?.customName || doc?.fileName || '';
  const mime = inferMimeType(label, doc?.mimeType);
  // A HEIC or other image no browser draws falls to the generic frame, and
  // the declared type wins over a misleading extension.
  if (mime?.startsWith('image/')) {
    return isBrowserRenderableImage(mime) ? 'image' : 'generic';
  }
  if (mime === 'application/pdf') return 'pdf';
  const ext = fileExtensionLower(label);
  if (ext === 'pdf') return 'pdf';
  if (['png', 'jpg', 'jpeg'].includes(ext)) return 'image';
  if (ext === 'doc' || ext === 'docx') return 'office';
  return 'generic';
}

export function isAllowedUploadFile(file: File): boolean {
  const ext = fileExtensionLower(file.name);
  return ext !== '' && ALLOWED_UPLOAD_EXTENSIONS.has(ext);
}

export interface DocStatusShape {
  isPersisted?: boolean;
  usedInExtraction?: boolean;
}

/**
 * Status-dot color, shared by the tab strip and the overflow dropdown so the two
 * can never drift: emerald once a doc is persisted and part of the AI
 * extraction, amber while it is still uploading or was added after extraction.
 */
export function docStatusDotClass(doc: DocStatusShape): string {
  return doc.isPersisted && doc.usedInExtraction
    ? 'bg-emerald-500'
    : 'bg-amber-500';
}

/** The amber status word for a doc, or null when it needs no annotation. */
export function docStatusLabel(doc: DocStatusShape): string | null {
  if (!doc.isPersisted) return 'Uploading';
  if (!doc.usedInExtraction) return 'Added after extraction';
  return null;
}

export interface DocTabPartition<T> {
  visible: T[];
  overflow: T[];
}

/**
 * Split document tabs into the run shown in the strip and the remainder that
 * collapses into the "+N more" dropdown.
 *
 * The first `visibleCount` documents stay in the strip. A document whose id is in
 * `forcedVisibleIds` (the active tab, plus any tab mid-rename) is guaranteed a
 * slot even when it sorts past the cut: it is pinned to the end of the visible
 * run so its hover actions stay reachable, and a trailing non-forced document is
 * bumped into the overflow set so the rendered count never exceeds the width the
 * strip measured room for. `visible.length <= visibleCount` always holds: when
 * more forced tabs want a slot than there are non-forced ones to bump, the
 * highest-priority ids win (earlier in `forcedVisibleIds` — callers pass the
 * active id before the editing id) and the rest fall back to overflow rather
 * than overflowing the strip. Ids that resolve to no document (e.g. the
 * transient empty active id between a delete and its re-selection) pin nothing.
 * Order is otherwise preserved and every document lands in exactly one of the
 * two lists.
 */
export function partitionDocTabs<T extends { id: string }>(
  docs: T[],
  visibleCount: number,
  forcedVisibleIds: ReadonlyArray<string | null | undefined> = []
): DocTabPartition<T> {
  const total = docs.length;
  const k = Math.max(0, Math.min(visibleCount, total));
  if (k >= total) {
    return { visible: [...docs], overflow: [] };
  }

  const present = new Set(docs.map(d => d.id));
  // Priority rank by position in forcedVisibleIds (first = highest). Only used to
  // pick winners when more tabs want pinning than there is room to bump; callers
  // pass the active id before the editing id, so the active tab always survives.
  const priority = new Map<string, number>();
  forcedVisibleIds.forEach((id, i) => {
    if (
      typeof id === 'string' &&
      id !== '' &&
      present.has(id) &&
      !priority.has(id)
    ) {
      priority.set(id, i);
    }
  });
  const forced = new Set(priority.keys());

  const firstRun = docs.slice(0, k);
  const firstRunIds = new Set(firstRun.map(d => d.id));
  const pinnedAll = docs.filter(
    d => forced.has(d.id) && !firstRunIds.has(d.id)
  );

  if (pinnedAll.length === 0) {
    return { visible: firstRun, overflow: docs.slice(k) };
  }

  // Only the non-forced first-run docs can be bumped to make room. If more tabs
  // want pinning than there are such slots, keep the highest-priority ones and
  // let the rest fall back to overflow so the visible count never exceeds k.
  const bumpableCount = firstRun.reduce(
    (n, d) => (forced.has(d.id) ? n : n + 1),
    0
  );
  let pinned = pinnedAll;
  if (pinnedAll.length > bumpableCount) {
    const kept = new Set(
      [...pinnedAll]
        .sort(
          (a, b) =>
            (priority.get(a.id) ?? Infinity) - (priority.get(b.id) ?? Infinity)
        )
        .slice(0, bumpableCount)
        .map(d => d.id)
    );
    pinned = pinnedAll.filter(d => kept.has(d.id)); // restore document order
  }

  // Bump the trailing non-forced first-run docs into overflow to make room for
  // the pinned tabs, keeping the visible count at k.
  const bumped = new Set<string>();
  let slots = pinned.length;
  for (let i = firstRun.length - 1; i >= 0 && slots > 0; i--) {
    if (!forced.has(firstRun[i].id)) {
      bumped.add(firstRun[i].id);
      slots -= 1;
    }
  }

  const visible = [...firstRun.filter(d => !bumped.has(d.id)), ...pinned];
  const visibleIds = new Set(visible.map(d => d.id));
  const overflow = docs.filter(d => !visibleIds.has(d.id));
  return { visible, overflow };
}
